import * as React from 'react';
import CollapsibleSection from '@sdk/components/ui/CollapsibleSection';
import ToastComponent from '@sdk/common/toast/ToastComponent';
import type { ClientFlags } from '@sdk/authentication/types';
import type { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import type { PersonasDict } from '@sdk/personaCollection/types/PersonasDict';
import type { SaveLaborCostSettings } from '@sdk/labor/types';
import type { LocationType } from '@sdk/locations/types/LocationType';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';

import type {
  RestangularEmployee,
  RestangularPosition,
} from '@web/onboarding/Checklist/DefineWagesModal/defineWagesModal.types';
import type {
  AddPersonaCollectionUser,
  EditPersonaCollectionGroup,
  NavigateToReports,
  NavigateToSchedule,
  OpenCreateShiftModal,
  UpdateOrgSettings,
  Event,
  ShowPublishShiftModal,
  ShowPublishShiftTooltip,
  UpdateWages,
} from '@web/onboarding/Checklist/onboardingChecklist.types';
import ChecklistHeader from '@web/onboarding/Checklist/components/ChecklistHeader';
import ChecklistContent from '@web/onboarding/Checklist/components/ChecklistContent';
import SetScheduleModal from '@web/onboarding/Checklist/SetScheduleModal/SetScheduleModal';
import LocationsModal from '@web/onboarding/Checklist/LocationsModal/LocationsModal';
import TimeclockModal from '@web/onboarding/Checklist/TimeclockModal/TimeclockModal';
import AddEmployeesModal from '@web/onboarding/Checklist/AddEmployeesModal/AddEmployeesModal';
import PublishScheduleModal from '@web/onboarding/Checklist/PublishScheduleModal/PublishScheduleModal';
import DefineWagesModal from '@web/onboarding/Checklist/DefineWagesModal/DefineWagesModal';
import { Container } from '@web/onboarding/Checklist/onboardingChecklist.styles';
import { useOnboardingChecklist } from '@web/onboarding/Checklist/onboardingChecklist.hooks';
import { ONBOARDING_TOAST_ID } from '@web/onboarding/Checklist/onboardingChecklist.consts';

export type Props = {
  currentUser: AuthUserType;
  locations: LocationType[];
  positions: RestangularPosition[];
  employees: RestangularEmployee[];
  clientFlags: ClientFlags;
  saveLaborCostSettingsInAngular: SaveLaborCostSettings;
  personasDict: PersonasDict;
  orgLocationsCount: number;
  addPersonaCollectionUser: AddPersonaCollectionUser;
  openCreateShiftModal: OpenCreateShiftModal;
  navigateToReports: NavigateToReports;
  navigateToSchedule: NavigateToSchedule;
  updateOrgSettings: UpdateOrgSettings;
  editPersonaCollectionGroup: EditPersonaCollectionGroup;
  isLoaded: boolean;
  showPublishShiftModal: ShowPublishShiftModal;
  showPublishShiftTooltip: ShowPublishShiftTooltip;
  allEvents: Event[];
  orgSettings: OrgSettings;
  autoOpenOnboarding: boolean;
  updateWages: UpdateWages;
};

const OnboardingChecklist: React.FC<React.PropsWithChildren<Props>> = ({
  currentUser,
  locations,
  positions,
  employees,
  clientFlags,
  saveLaborCostSettingsInAngular,
  personasDict,
  orgLocationsCount,
  addPersonaCollectionUser,
  openCreateShiftModal,
  navigateToReports,
  navigateToSchedule,
  updateOrgSettings,
  editPersonaCollectionGroup,
  isLoaded,
  showPublishShiftModal,
  showPublishShiftTooltip,
  allEvents,
  orgSettings,
  autoOpenOnboarding,
  updateWages,
}) => {
  const {
    supportedSteps,
    stepsConfig,
    isOpen,
    setIsOpen,
    setScheduleModalRef,
    timeclockModalRef,
    locationsModalRef,
    timeclockSaveCallback,
    locationsWithoutAddress,
    addEmployeesModalRef,
    publishScheduleModalRef,
    defineWagesModalRef,
  } = useOnboardingChecklist({
    clientFlags,
    locations,
    openCreateShiftModal,
    navigateToReports,
    navigateToSchedule,
    personasDict,
    isLoaded,
    showPublishShiftModal,
    showPublishShiftTooltip,
    allEvents,
    orgSettings,
    autoOpenOnboarding,
    employees,
    positions,
  });

  return (
    <>
      <Container isOpen={isOpen}>
        <CollapsibleSection
          isActive={isOpen}
          headerItem={
            <ChecklistHeader
              isOpen={isOpen}
              userName={currentUser?.name}
              steps={supportedSteps}
              config={stepsConfig}
              onClick={(): void => setIsOpen(!isOpen)}
            />
          }
          contentItem={<ChecklistContent steps={supportedSteps} config={stepsConfig} />}
        />
      </Container>

      <SetScheduleModal
        ref={setScheduleModalRef}
        orgSettings={currentUser.orgSettings}
        saveLaborCostSettingsInAngular={saveLaborCostSettingsInAngular}
        personasDict={personasDict}
        updateOrgSettings={updateOrgSettings}
        orgLocationsCount={orgLocationsCount}
      />

      <TimeclockModal
        timeclockSaveCallback={timeclockSaveCallback}
        ref={timeclockModalRef}
        orgSettings={orgSettings}
        updateOrgSettings={updateOrgSettings}
        personasDict={personasDict}
        hasLocationsWithoutAddress={!!locationsWithoutAddress?.length}
      />

      <AddEmployeesModal
        ref={addEmployeesModalRef}
        currentUser={currentUser}
        locations={locations}
        addPersonaCollectionUser={addPersonaCollectionUser}
      />

      {!!locationsWithoutAddress?.length && (
        <LocationsModal
          ref={locationsModalRef}
          locations={locationsWithoutAddress}
          editPersonaCollectionGroup={editPersonaCollectionGroup}
        />
      )}

      <PublishScheduleModal
        ref={publishScheduleModalRef}
        openCreateShiftModal={openCreateShiftModal}
      />

      <DefineWagesModal
        ref={defineWagesModalRef}
        employees={employees}
        positions={positions}
        openAddEmployeesStep={addEmployeesModalRef.current?.open}
        updateWages={updateWages}
      />
      <ToastComponent containerId={ONBOARDING_TOAST_ID} />
    </>
  );
};

export default OnboardingChecklist;
